/**
 * Generate Mermaid flowchart syntax from React Flow nodes and edges
 */

import type {
  MermaidDirection,
  MermaidEdge,
  MermaidEdgeArrow,
  MermaidEdgeStyle,
  MermaidNode,
  MermaidNodeShape,
} from "./mermaid-types"

function getShapeSyntax(shape: MermaidNodeShape): { open: string; close: string } {
  const shapeSyntaxMap: Record<MermaidNodeShape, { open: string; close: string }> = {
    rectangle: { open: "[", close: "]" },
    rounded: { open: "(", close: ")" },
    stadium: { open: "([", close: "])" },
    subroutine: { open: "[[", close: "]]" },
    cylinder: { open: "[(", close: ")]" },
    circle: { open: "((", close: "))" },
    asymmetric: { open: ">", close: "]" },
    rhombus: { open: "{", close: "}" },
    hexagon: { open: "{{", close: "}}" },
    parallelogram: { open: "[/", close: "/]" },
    parallelogramAlt: { open: "[\\", close: "\\]" },
    trapezoid: { open: "[/", close: "\\]" },
    trapezoidAlt: { open: "[\\", close: "/]" },
    doubleCircle: { open: "(((", close: ")))" },
  }
  return shapeSyntaxMap[shape]
}

function getEdgeSyntax(
  style: MermaidEdgeStyle,
  sourceArrow: MermaidEdgeArrow,
  targetArrow: MermaidEdgeArrow
): string {
  const hasSourceArrow = sourceArrow === "arrow"
  const hasTargetArrow = targetArrow === "arrow"

  switch (style) {
    case "thick":
      if (hasSourceArrow && hasTargetArrow) return "<==>"
      if (hasTargetArrow) return "==>"
      if (hasSourceArrow) return "<=="
      return "==="
    case "dotted":
      if (hasSourceArrow && hasTargetArrow) return "<-.->"
      if (hasTargetArrow) return "-.->"
      if (hasSourceArrow) return "<-.-"
      return "-.-"
    case "invisible":
      return "~~~"
    case "solid":
    default:
      if (hasSourceArrow && hasTargetArrow) return "<-->"
      if (hasTargetArrow) return "-->"
      if (hasSourceArrow) return "<--"
      return "---"
  }
}

function escapeLabel(label: string): string {
  const needsEscaping = /[\\[\](){}|<>"]/.test(label)
  if (needsEscaping) {
    const escapedLabel = label.replace(/\\/g, "\\\\").replace(/"/g, '\\"')
    return `"${escapedLabel}"`
  }
  return label
}
// eslint-disable-next-line max-params
export function generateMermaidCode(
  nodes: MermaidNode[],
  edges: MermaidEdge[],
  direction: MermaidDirection = "TD",
  subgraphs?: Map<string, { id: string; label: string; nodeIds: string[] }>
): string {
  const lines: string[] = [`flowchart ${direction}`, ""]

  const nodesInSubgraphs = new Set<string>()
  if (subgraphs && subgraphs.size > 0) {
    for (const [, subgraph] of subgraphs) {
      for (const nodeId of subgraph.nodeIds) {
        nodesInSubgraphs.add(nodeId)
      }
    }
  }

  const topLevelNodes = nodes.filter((node) => !nodesInSubgraphs.has(node.id))
  for (const node of topLevelNodes) {
    const { open, close } = getShapeSyntax(node.data.shape)
    const label = escapeLabel(node.data.label)
    lines.push(`    ${node.id}${open}${label}${close}`)
  }

  if (subgraphs && subgraphs.size > 0) {
    lines.push("")
    for (const [, subgraph] of subgraphs) {
      lines.push(`    subgraph ${subgraph.id} [${subgraph.label}]`)
      for (const nodeId of subgraph.nodeIds) {
        const node = nodes.find((n) => n.id === nodeId)
        if (node) {
          const { open, close } = getShapeSyntax(node.data.shape)
          const label = escapeLabel(node.data.label)
          lines.push(`        ${node.id}${open}${label}${close}`)
        }
      }
      lines.push("    end")
    }
  }

  if (edges.length > 0) {
    lines.push("")
    for (const edge of edges) {
      const edgeData = edge.data || { style: "solid", sourceArrow: "none", targetArrow: "arrow" }
      const edgeSyntax = getEdgeSyntax(edgeData.style, edgeData.sourceArrow, edgeData.targetArrow)

      if (edge.label || edgeData.label) {
        const label = escapeLabel(String(edge.label || edgeData.label))
        lines.push(`    ${edge.source} ${edgeSyntax}|${label}| ${edge.target}`)
      } else {
        lines.push(`    ${edge.source} ${edgeSyntax} ${edge.target}`)
      }
    }
  }

  return lines.join("\n")
}

export function generateNewNodeId(existingNodes: MermaidNode[]): string {
  const existingIds = new Set(existingNodes.map((node) => node.id))
  const letters = "ABCDEFGHIJKLMNOPQRSTUVWXYZ"

  for (const letter of letters) {
    if (!existingIds.has(letter)) {
      return letter
    }
  }

  for (let index = 1; index <= 100; index++) {
    for (const letter of letters) {
      const id = `${letter}${index}`
      if (!existingIds.has(id)) {
        return id
      }
    }
  }

  return `node_${Date.now()}`
}
